// Home Component - Landing Page with Conversation Options
// Phase 11: Pre Go-Live Improvements
// © 2025 precatio, llc

import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import AuthModal from './AuthModal';
import ConversationHistory from './ConversationHistory';
import SubscriptionModal from './SubscriptionModal';
import AboutIggyModal from './AboutIggyModal';
import { CalendarCheckIcon, PersonPrayingIcon, NineIcon, SpeechBubbleIcon, LightbulbIcon, BibleIcon } from './Icons';
import './Home.css';

const Home = ({ onSelectConversation, onResumeConversation, initialAuthModal, onAuthModalShown }) => {
  const { user, isAuthenticated, logout, hasPremiumAccess, refreshSubscription } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [pendingConversation, setPendingConversation] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);

  // Handle initial auth modal from URL redirect (e.g., after password reset)
  useEffect(() => {
    if (initialAuthModal) {
      setAuthModalMode(initialAuthModal);
      setShowAuthModal(true);
      if (onAuthModalShown) {
        onAuthModalShown();
      }
    }
  }, [initialAuthModal, onAuthModalShown]);

  // Check for subscription success in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      if (refreshSubscription) {
        refreshSubscription();
      }
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [refreshSubscription]);

  const conversations = [
    {
      id: 'examen',
      title: 'Daily Examen',
      description: 'Review your day with God',
      icon: CalendarCheckIcon,
      premium: false
    },
    {
      id: 'prayers',
      title: 'Common Prayers',
      description: 'Pray with Iggy',
      icon: PersonPrayingIcon,
      premium: false
    },
    {
      id: 'novena',
      title: 'Custom Novena',
      description: 'Create a 9-day prayer journey',
      icon: NineIcon,
      premium: false
    },
    {
      id: 'gospelprayer',
      title: 'Gospel Prayer',
      description: 'Imaginative prayer with Scripture',
      icon: BibleIcon,
      premium: true
    },
    {
      id: 'askiggy',
      title: 'Ask Iggy',
      description: 'Your AI spiritual companion',
      icon: SpeechBubbleIcon,
      premium: true
    },
    {
      id: 'discernment',
      title: 'Discernment',
      description: 'Navigate a decision with prayer',
      icon: LightbulbIcon,
      premium: true
    }
  ];

  const handleConversationClick = (conversation) => {
    if (conversation.premium && !isAuthenticated) {
      // Need to sign up for premium features
      setPendingConversation(conversation.id);
      setAuthModalMode('signup');
      setShowAuthModal(true);
    } else if (conversation.premium && !hasPremiumAccess()) {
      // Trial expired, need subscription - show subscription modal
      setShowSubscriptionModal(true);
    } else {
      onSelectConversation(conversation.id);
    }
  };

  const handleAuthSuccess = () => {
    // If user was trying to access a premium feature, continue to it
    if (pendingConversation) {
      onSelectConversation(pendingConversation);
      setPendingConversation(null);
    }
  };

  const openAuthModal = (mode) => {
    setAuthModalMode(mode);
    setShowAuthModal(true);
  };

  const getUserTierDisplay = () => {
    if (!user) return null;
    const tier = user.subscriptionTier || 'pilgrim';
    const tierNames = {
      pilgrim: 'Pilgrim',
      disciple: 'Disciple',
      companion: 'Companion',
      retreatant: 'Retreatant'
    };
    return tierNames[tier];
  };

  return (
    <div className="home-container">
      {/* Auth Section - Top Right */}
      <div className="home-auth">
        {isAuthenticated ? (
          <div className="auth-user-info">
            <button onClick={() => setShowHistory(true)} className="auth-history-btn">
              History
            </button>
            <span className="auth-email">{user.email}</span>
            
            {/* Subscription Status Badge */}
            {user.subscriptionStatus === 'trial' && user.trialDaysRemaining > 0 ? (
              <span 
                className="auth-trial-badge clickable"
                onClick={() => setShowSubscriptionModal(true)}
              >
                {user.trialDaysRemaining} days left in trial
              </span>
            ) : user.subscriptionStatus === 'active' ? (
              <span 
                className="auth-subscription-badge clickable"
                onClick={() => setShowSubscriptionModal(true)}
              >
                {getUserTierDisplay()}
              </span>
            ) : user.subscriptionStatus === 'trial' && user.trialDaysRemaining <= 0 ? (
              <span 
                className="auth-expired-badge clickable"
                onClick={() => setShowSubscriptionModal(true)}
              >
                Trial Expired
              </span>
            ) : (
              <span 
                className="auth-free-badge clickable"
                onClick={() => setShowSubscriptionModal(true)}
              >
                Free Plan
              </span>
            )}
            
            <button onClick={logout} className="auth-logout-btn">
              Log Out
            </button>
          </div>
        ) : (
          <div className="auth-buttons">
            <button onClick={() => openAuthModal('login')} className="auth-login-btn">
              Log In
            </button>
            <button onClick={() => openAuthModal('signup')} className="auth-signup-btn">
              Sign Up Free
            </button>
          </div>
        )}
      </div>

      <header className="home-header">
        <img 
          src="/iggy-icon.jpg" 
          alt="Iggy" 
          className="iggy-logo clickable" 
          onClick={() => setShowAboutModal(true)}
          title="Click to learn about Iggy"
        />
        <h1 className="home-title">PRECAT.IO</h1>
        <p className="home-tagline">
          Every step toward God. Guided by{' '}
          <span 
            className="iggy-link" 
            onClick={() => setShowAboutModal(true)}
            title="Click to learn about Iggy"
          >
            Iggy
          </span>.
        </p>
      </header>

      <main className="home-main">
        <div className="conversation-grid">
          {conversations.map((conversation) => {
            const IconComponent = conversation.icon;
            const needsAuth = conversation.premium && !isAuthenticated;
            const needsSubscription = conversation.premium && isAuthenticated && !hasPremiumAccess();
            
            return (
              <button
                key={conversation.id}
                className={`conversation-card ${conversation.premium ? 'premium' : ''}`}
                onClick={() => handleConversationClick(conversation)}
              >
                <div className="card-icon">
                  <IconComponent size={48} />
                </div>
                <h2 className="card-title">{conversation.title}</h2>
                <p className="card-description">{conversation.description}</p>
                {needsAuth && (
                  <span className="card-badge">Sign up for free trial</span>
                )}
                {needsSubscription && (
                  <span className="card-badge subscription">Upgrade</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Account Benefits Prompt - Only show to non-authenticated users */}
        {!isAuthenticated && (
          <div className="account-prompt">
            <p>
              <strong>Create a free account</strong> to save your conversations 
              and continue on any device. Plus, get 2 weeks free access to 
              Discernment and Ask Iggy!
            </p>
            <button 
              onClick={() => openAuthModal('signup')} 
              className="account-prompt-btn"
            >
              Sign Up Free
            </button>
          </div>
        )}

        {/* Upgrade Prompt - Show to users with expired trial */}
        {isAuthenticated && user.subscriptionStatus === 'trial' && user.trialDaysRemaining <= 0 && (
          <div className="upgrade-prompt">
            <p>
              <strong>Your free trial has ended.</strong> Subscribe to continue 
              using Ask Iggy and Discernment.
            </p>
            <button 
              onClick={() => setShowSubscriptionModal(true)} 
              className="upgrade-prompt-btn"
            >
              View Plans
            </button>
          </div>
        )}
      </main>

      <footer className="home-footer">
        <p>© 2025 precatio, llc</p>
        <div className="footer-links">
          <a href="/privacy" className="footer-link">Privacy Policy</a>
          <span className="footer-divider">|</span>
          <a href="https://directionalfaith.com" target="_blank" rel="noopener noreferrer" className="footer-link">
            Human spiritual direction at DirectionalFaith.com
          </a>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setPendingConversation(null);
        }}
        initialMode={authModalMode}
        onSuccess={handleAuthSuccess}
      />

      {/* Conversation History */}
      {showHistory && (
        <ConversationHistory
          onSelectConversation={(conv) => {
            setShowHistory(false);
            if (onResumeConversation) {
              onResumeConversation(conv);
            }
          }}
          onClose={() => setShowHistory(false)}
        />
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSuccess={() => {
          setShowSubscriptionModal(false);
          if (refreshSubscription) {
            refreshSubscription();
          }
        }}
      />

      {/* About Iggy Modal */}
      <AboutIggyModal
        isOpen={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />
    </div>
  );
};

export default Home;
